import React from "react";
import Toggle from "../../../../components-ui/Toggle";
import FieldHelpTooltip from "../../../../../components/common/FieldHelpTooltip";
import {
  RegistrationMemberType,
  RegistrationRequirementLevel,
  ServiceRegistrationRequirementDto,
} from "../../../../types/types";
const REGISTRATION_REQUIREMENT_LEVELS: Array<{
  value: RegistrationRequirementLevel;
  label: string;
}> = [
  { value: "Hidden", label: "Hidden" },
  { value: "Optional", label: "Optional" },
  { value: "Required", label: "Required" },
];

const REQUIREMENT_SHORT_LABELS: Record<RegistrationRequirementLevel, string> = {
  Hidden: "Hide",
  Optional: "Opt",
  Required: "Req",
};

const REQUIREMENT_GROUPS = [
  {
    title: "Person details",
    description: "Core information about the person or primary family contact.",
    componentKeys: ["PrimaryContact", "ContactDetails", "Address"],
  },
  {
    title: "Family and child details",
    description: "Household relationships and child-specific information.",
    componentKeys: ["Household", "YouthProfile", "EmergencyContact"],
  },
  {
    title: "Service registration",
    description: "How the person is enrolled and classified in this service.",
    componentKeys: ["ServiceMembership", "ServiceRoles"],
  },
  {
    title: "Completion",
    description: "Agreements, consent questions and final review.",
    componentKeys: ["Agreements", "Consents", "Review"],
  },
];

interface RegistrationStepProps {
  memberTypes: Array<{ value: RegistrationMemberType; label: string }>;
  publicRegistrationEnabled: boolean;
  memberType: RegistrationMemberType;
  requirements: Record<
    RegistrationMemberType,
    ServiceRegistrationRequirementDto[]
  >;
  partyTypes: Record<RegistrationMemberType, "Person" | "Family">;
  loading: boolean;
  componentText: (key: string) => { label: string; description: string };
  onMemberTypeChange: (type: RegistrationMemberType) => void;
  onPublicRegistrationChange: (enabled: boolean) => void;
  onPartyTypeChange: (type: "Person" | "Family") => void;
  onRequirementChange: (
    key: string,
    level: RegistrationRequirementLevel,
  ) => void;
}

export function ServiceRegistrationStep({
  memberTypes,
  publicRegistrationEnabled,
  memberType,
  requirements,
  partyTypes,
  loading,
  componentText,
  onMemberTypeChange,
  onPublicRegistrationChange,
  onPartyTypeChange,
  onRequirementChange,
}: RegistrationStepProps) {
  const current = requirements[memberType] ?? [];
  const groups = REQUIREMENT_GROUPS.map((group) => ({
    ...group,
    requirements: group.componentKeys
      .map((key) => current.find((item) => item.componentKey === key))
      .filter(Boolean) as ServiceRegistrationRequirementDto[],
  })).filter((group) => group.requirements.length);
  return (
    <div className="card">
      <div className="service-registration-header">
        <div>
          <div className="card-title">Registration Requirements</div>
          <div className="form-help-text">
            Configure which sections are available for each type of member.
          </div>
        </div>
        <div className="service-public-registration-setting">
          <Toggle
            name="isPublicRegistrationEnabled"
            label="Public registration"
            checked={publicRegistrationEnabled}
            onChange={onPublicRegistrationChange}
          />
          <FieldHelpTooltip text="Allow people to register themselves for this service using the public registration form." />
        </div>
      </div>
      <div className="service-registration-member-selector">
        <div className="form-label">Configure requirements for</div>
        <div className="service-registration-member-tabs">
          {memberTypes.map((type) => (
            <button
              key={type.value}
              type="button"
              className={`service-registration-member-tab ${memberType === type.value ? "active" : ""}`}
              onClick={() => onMemberTypeChange(type.value)}
            >
              {type.label}
            </button>
          ))}
        </div>
      </div>
      <div className="service-registration-model-field">
        <label className="form-label" htmlFor="service-registration-model">
          Registration model
          <FieldHelpTooltip
            text={
              partyTypes[memberType] === "Family"
                ? "Collect a primary contact, household and service users in one registration."
                : "Register one person directly without creating a household."
            }
          />
        </label>
        <select
          id="service-registration-model"
          className="form-input"
          value={partyTypes[memberType]}
          onChange={(event) =>
            onPartyTypeChange(event.target.value as "Person" | "Family")
          }
        >
          <option value="Person">Single person</option>
          {memberType === "ServiceUser" && (
            <option value="Family">Family / household</option>
          )}
        </select>
      </div>
      {memberType === "ServiceUser" && partyTypes.ServiceUser === "Family" && (
        <div className="service-registration-included-note">
          <strong>Includes primary contact</strong>
          <span>
            Primary contact details are collected as part of this registration
            journey and do not need a separate flow.
          </span>
        </div>
      )}
      {loading ? (
        <div className="field-hint">Loading registration requirements...</div>
      ) : (
        <div className="registration-requirements-board">
          {groups.map((group) => (
            <section
              key={group.title}
              className="registration-requirement-group"
            >
              <div className="registration-requirement-group-header">
                <div className="registration-requirement-group-title">
                  {group.title}
                </div>
                <div className="field-hint" style={{ marginBottom: 0 }}>
                  {group.description}
                </div>
              </div>
              <div className="registration-requirement-card-list">
                {group.requirements.map((requirement) => {
                  const text = componentText(requirement.componentKey);
                  return (
                    <article
                      key={requirement.componentKey}
                      className={`registration-requirement-card registration-requirement-card--${requirement.requirementLevel.toLowerCase()}`}
                    >
                      <div className="registration-requirement-card-copy">
                        <div className="service-registration-requirement-name">
                          {text.label}
                        </div>
                        {text.description && (
                          <div
                            className="field-hint"
                            style={{ marginBottom: 0 }}
                          >
                            {text.description}
                          </div>
                        )}
                      </div>
                      <div
                        className="registration-requirement-segmented"
                        role="radiogroup"
                        aria-label={`${text.label} requirement level`}
                      >
                        {REGISTRATION_REQUIREMENT_LEVELS.map((level) => (
                          <button
                            key={level.value}
                            type="button"
                            role="radio"
                            aria-checked={
                              requirement.requirementLevel === level.value
                            }
                            className={`registration-requirement-segment ${requirement.requirementLevel === level.value ? "active" : ""}`}
                            onClick={() =>
                              onRequirementChange(
                                requirement.componentKey,
                                level.value,
                              )
                            }
                          >
                            <span className="registration-requirement-segment-label">
                              {level.label}
                            </span>
                            <span className="registration-requirement-segment-short-label">
                              {REQUIREMENT_SHORT_LABELS[level.value]}
                            </span>
                          </button>
                        ))}
                      </div>
                    </article>
                  );
                })}
              </div>
            </section>
          ))}
        </div>
      )}
    </div>
  );
}
